import React from 'react';
import { Shield, Mail, FileText, Users, Lock, Globe } from 'lucide-react';

export default function PrivacyPolicy() {
  const sections = [
    {
      icon: FileText,
      title: "Interpretation and Definitions",
      content: [
        {
          subtitle: "Interpretation",
          text: "The words whose initial letters are capitalized have specific meanings under the following conditions. The definitions provided below apply regardless of whether these terms appear in singular or plural form."
        },
        {
          subtitle: "Definitions",
          list: [
            { term: "Account", definition: "means a unique account created for you to access our Service or parts of our Service." },
            { term: "Affiliate", definition: "refers to an entity that controls, is controlled by, or is under common control with a party. \"Control\" in this context means ownership of 50% or more of the shares, equity interest, or other securities entitled to vote for the election of directors or other managing authorities." },
            { term: "Application", definition: "pertains to the software program provided by the Company, which you download onto any electronic device." },
            { term: "Company", definition: "(referred to as \"the Company,\" \"We,\" \"Us,\" or \"Our\" in this Agreement) refers to PAIDAR, located in Sindh, Pakistan." },
            { term: "Country", definition: "refers to Pakistan." },
            { term: "Device", definition: "denotes any electronic device capable of accessing the Service, such as a computer, cellphone, or digital tablet." },
            { term: "Personal Data", definition: "encompasses any information that relates to an identified or identifiable individual." },
            { term: "Service", definition: "pertains to the Application." },
            { term: "Service Provider", definition: "signifies any natural or legal person who processes data on behalf of the Company. This may include third-party companies or individuals engaged by the Company to facilitate the Service and provide it on our behalf." },
            { term: "Usage Data", definition: "pertains to information collected automatically. This information is generated through the utilization of the Service or originates from the Service infrastructure itself." },
            { term: "You", definition: "refer to the individual accessing or using the Service. It also applies to the company or any other legal entity on whose behalf an individual is accessing or using the Service, as applicable." }
          ]
        }
      ]
    },
    {
        icon: Users,
        title: "Collecting and Using Your Personal Data",
        content: [
            {
                subtitle: "Types of Data Collected",
                subsections: [
                    {
                        title: "Personal Data",
                        text: "While utilizing our Service, we may request specific personally identifiable information from you that can be utilized to contact or identify you. Such personally identifiable information may include, but is not limited to Usage Data."
                    },
                    {
                        title: "Usage Data",
                        text: (
                            <>
                            Usage Data is gathered automatically when using the Service. It may include information such as your Device's Internet Protocol (IP) address, browser type, browser version, and the pages of our Service you visit, the date and time of your visit, the duration of your visit on those pages, unique device identifiers, and other diagnostic data.
                            <br /><br />
                            When you access the Service via a mobile device, certain information may be automatically collected, including but not limited to the type of mobile device you use, your mobile device's unique ID, your mobile device's IP address, your mobile operating system, the type of mobile Internet browser you employ, unique device identifiers, and other diagnostic data.
                            <br /><br />
                            Additionally, we may collect information sent by your browser each time you visit our Service or access it via a mobile device.
                            </>
                        )
                    }
                ]
            }
        ]
    },
    {
      icon: Lock,
      title: "Use of Your Personal Data",
      purposes: [
        { title: "Service Provision and Maintenance", text: "To provide and maintain our Service, including monitoring the usage of our Service." },
        { title: "Account Management", text: "To manage your account, allowing you to access various functionalities of the Service available to registered users." },
        { title: "Contractual Performance", text: "For the performance of a contract, including the development, compliance, and execution of purchase contracts for products, items, or services you have acquired or any other contract with us through the Service." },
        { title: "Communication", text: "To contact you via email, telephone calls, SMS, or equivalent forms of electronic communication, such as push notifications from our mobile application." },
        { title: "News and Offers", text: "To provide you with news, special offers, and general information about goods, services, and events that are similar to those you have already purchased or inquired about." },
        { title: "Request Management", text: "To attend to and manage your requests to us." },
        { title: "Business Transfers", text: "We may use your information to assess or undertake mergers, divestitures, restructurings, reorganizations, dissolutions, or other sales or transfers of some or all of our assets." },
        { title: "Other Purposes", text: "We may use your information for other purposes, including data analysis, identification of usage trends, evaluation of the effectiveness of our promotional campaigns, and the enhancement of our Service." }
      ]
    },
    {
      icon: Globe,
      title: "Sharing of Your Personal Information",
      sharing: [
        { title: "With Service Providers", text: "We may share your personal information with Service Providers to monitor and analyze the use of our Service and to contact you." },
        { title: "Business Transfers", text: "We may share or transfer your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of our business by another company." },
        { title: "With Affiliates", text: "We may share your information with our affiliates, subject to their adherence to this Privacy Policy." },
        { title: "With Business Partners", text: "We may share your information with our business partners to provide you with specific products, services, or promotions." },
        { title: "With Other Users", text: "When you share personal information or engage in interactions within public areas, other users may view this information." },
        { title: "With Your Consent", text: "We may disclose your personal information for any other purpose when we have obtained your explicit consent." }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">PAIDAR Privacy Policy</h1>
          <p className="text-gray-600">Effective Date: October 15, 2025</p>
          <p className="mt-4 text-gray-700 max-w-3xl mx-auto">
            This policy outlines our procedures for collecting, using, and disclosing your information when you use our services. 
            We are committed to safeguarding your privacy and ensuring that your personal information is handled securely and in compliance with applicable laws.
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {sections.map((section, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mt-2">{section.title}</h2>
              </div>

              {section.content && section.content.map((item, i) => (
                <div key={i} className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">{item.subtitle}</h3>
                  {item.text && <p className="text-gray-700 leading-relaxed">{item.text}</p>}
                  {item.list && (
                    <dl className="space-y-4 mt-4">
                      {item.list.map((def, j) => (
                        <div key={j} className="border-l-4 border-blue-200 pl-4 py-2">
                          <dt className="font-semibold text-gray-900">{def.term}</dt>
                          <dd className="text-gray-700 mt-1">{def.definition}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {item.subsections && (
                    <div className="space-y-4 mt-4">
                      {item.subsections.map((sub, k) => (
                        <div key={k} className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">{sub.title}</h4>
                          <p className="text-gray-700">{sub.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {section.purposes && (
                <div className="space-y-3">
                  {section.purposes.map((purpose, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                      <div>
                        <span className="font-semibold text-gray-900">{purpose.title}:</span>
                        <span className="text-gray-700"> {purpose.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {section.sharing && (
                <div className="space-y-3">
                  {section.sharing.map((share, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                      <div>
                        <span className="font-semibold text-gray-900">{share.title}:</span>
                        <span className="text-gray-700"> {share.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Additional Sections */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Retention of Your Personal Data</h2>
            <p className="text-gray-700 leading-relaxed">
              The Company will only retain your Personal Data for as long as it is necessary for the purposes outlined in this Privacy Policy. 
              We will keep and use your Personal Data to the extent required to fulfill our legal obligations, resolve disputes, and enforce our legal agreements and policies.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed">
              Additionally, the Company may retain Usage Data for internal analysis purposes. Usage Data is typically held for a shorter duration, 
              except when it is utilized to enhance security, improve Service functionality, or when legal obligations mandate longer retention periods.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Transfer of Your Personal Data</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information, including Personal Data, may be processed at the Company's operating offices and in other locations where the parties involved in processing are situated. 
              This means that such information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction. The data protection laws in these locations may differ from those in your jurisdiction.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed">
              By consenting to this Privacy Policy and submitting your information, you agree to the transfer of your information as described.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed">
              The Company will take all reasonable steps to ensure that your data is treated securely and in accordance with this Privacy Policy. 
              No transfer of your Personal Data will occur to any organization or country unless adequate controls are in place, including the security of your data and other personal information.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclosure of Your Personal Data</h2>
            <h5 className="text font-bold text-gray-900 mb-4">Business Transactions:</h5>
            <p className="text-gray-700 leading-relaxed">
              If the Company is involved in a merger, acquisition, or asset sale, your Personal Data may be transferred. We will provide notice before your Personal Data is transferred and becomes subject to a different Privacy Policy.
            </p>
            <br />
            <h5 className="text font-bold text-gray-900 mb-4">Law enforcement:</h5>
            <p className="text-gray-700 leading-relaxed">
              Under certain circumstances, the Company may be required to disclose your Personal Data if mandated by law or in response to valid requests by public authorities (e.g., a court or a government agency).
            </p>
            <br />
            <h5 className="text font-bold text-gray-900 mb-4">Other legal requirements:</h5>
            <p className="text-gray-700 leading-relaxed">
              The Company may disclose your Personal Data in good faith, believing that such action is necessary to:
            </p>
            <ul className="list-disc list-inside mt-2 text-gray-700 leading-relaxed">
              <li>Comply with a legal obligation</li>
              <li>Protect and defend the rights or property of the Company</li>
              <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
              <li>Protect the personal safety of users of the Service or the public</li>
              <li>Protect against legal liability</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Security of Your Personal Data</h2>
            <p className="text-gray-700 leading-relaxed">
              The security of your Personal Data is important to us, but it's essential to remember that no method of transmission over the Internet 
              or electronic storage is 100% secure. Although we make every effort to employ commercially acceptable means to protect your Personal Data, 
              we cannot provide an absolute guarantee of its security.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Service is not intended for individuals under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If you are a parent or guardian and you believe that your child has provided us with Personal Data, please contact us. 
              If we become aware that we have collected Personal Data from anyone under the age of 13 without proper parental consent, 
              we will take steps to remove that information from our servers. In cases where we need to rely on consent as the legal basis for processing your information, and your country requires parental consent, we may request your parent's consent before collecting and using that information.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Links to Other Websites</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Service may include links to third-party websites that are not operated by us. If you click on a third-party link, you will be directed to that third party's site. We strongly recommend reviewing the Privacy Policy of every website you visit.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed">
              We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to this Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may periodically update our Privacy Policy. Any changes will be communicated by posting the new Privacy Policy on this page. We will also notify you via email and/or provide a prominent notice on our 
              Service before the changes take effect. The "Last updated" date at the top of this Privacy Policy will be revised accordingly.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed">
              We recommend reviewing this Privacy Policy regularly for any updates. Changes to this Privacy Policy become effective when posted on this page.
            </p>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Contact Us</h2>
            </div>
            <p className="mb-4">If you have any questions about this Privacy Policy, you can contact us:</p>
            <a href="mailto:developer.paidar@gmail.com" className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
              <Mail className="w-5 h-5" />
              developer.paidar@gmail.com
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-600 text-sm">
          <p>© 2025 PAIDAR. All rights reserved.</p>
          <p className="mt-2">Last updated: October 15, 2025</p>
        </div>
      </div>
    </div>
  );
}